import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import SerialPostDialog from '../serial/SerialPostDialog'; // F6b
import { SalesReturnService } from '../../services/salesReturn.service';
import { CreditNoteService } from '../../services/creditNote.service';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { CreditNoteDraft, RETURN_STATUS_COLORS, SalesReturn } from '../../interfaces/sales/salesReturn.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ReassignDialog from './scope/ReassignDialog';
import { leafAccounts } from '../../utils/postingAccounts';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

const SalesReturnDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution();
  const canManage = usePermission(PERMISSIONS.SALES_RETURN_MANAGE);
  const canPost = usePermission(PERMISSIONS.SALES_RETURN_POST);   // S1
  const canSettings = usePermission(PERMISSIONS.SALES_SETTINGS_MANAGE);
  const canReassign = usePermission(PERMISSIONS.SALES_REASSIGN);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [r, setR] = useState<SalesReturn | null>(null);
  const [busy, setBusy] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [draft, setDraft] = useState<CreditNoteDraft | null>(null);
  const [damagedAccount, setDamagedAccount] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);

  const load = useCallback(async () => {
    try { setR(await SalesReturnService.get(id!)); } catch (e) { toast.showApiError(e, 'Failed to load the return'); }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canSettings || !institutionId) return;
    (async () => {
      try {
        const [s, accs] = await Promise.all([SalesReturnService.getSettings(institutionId), AccountService.getAllAccounts()]);
        setDamagedAccount(s?.damagedGoodsAccountId ?? '');
        setAccounts(accs ?? []);
      } catch (e) { toast.showApiError(e, 'Failed to load the damaged-goods setting'); }
    })();
  }, [canSettings, institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn: () => Promise<unknown>, ok: string, fail: string): Promise<boolean> => {
    try { setBusy(true); await fn(); toast.showSuccess(ok); await load(); return true; } catch (e) { toast.showApiError(e, fail); return false; } finally { setBusy(false); }
  };

  const openCreditNote = async () => {
    try { setDraft(await SalesReturnService.creditNoteDraft(id!)); } catch (e) { toast.showApiError(e, 'Failed to prepare the credit note'); }
  };
  const issueCreditNote = async () => {
    if (!draft) return;
    await run(async () => {
      const note = await CreditNoteService.create({
        salesInvoiceId: draft.salesInvoiceId, noteDate: draft.noteDate, reason: draft.reason, salesReturnId: draft.salesReturnId,
        lines: draft.lines.map(l => ({ salesInvoiceLineId: l.salesInvoiceLineId, quantity: l.quantity })),
      });
      setDraft(null);
      navigate(`/sales/credit-notes/${note.id}`);
    }, 'Credit note issued', 'Failed to issue the credit note');
  };
  const saveAccount = () => run(() => SalesReturnService.updateSettings(institutionId!, damagedAccount || null),
    'Damaged-goods account saved', 'Failed to save the damaged-goods account');

  if (!r) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  const hasDamaged = r.lines.some(l => l.condition === 'DAMAGED');
  const activeNote = r.creditNotes.find(c => c.status === 'POSTED');

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.returnDetail.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5">
          Customer Return {r.returnNo ?? ''}{' '}
          <Chip size="small" label={humanizeEnum(r.status)} color={RETURN_STATUS_COLORS[r.status]} />
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>Salesperson: {r.salespersonName ?? 'Unassigned'}</Typography>
          {canReassign && <Button data-help="sales.returnDetail.reassign" onClick={() => setReassignOpen(true)}>Reassign</Button>}
          {reassignOpen && (
            <ReassignDialog open type="returns" id={r.id} title={`Return ${r.returnNo ?? 'draft'}`} currentName={r.salespersonName}
              onClose={() => setReassignOpen(false)} onDone={load} />
          )}
          <Button data-help="sales.returnDetail.challan" onClick={() => navigate(`/sales/challans/${r.deliveryChallanId}`)}>Challan {r.challanNo}</Button>
          {canManage && r.status === 'DRAFT' && <Button disabled={busy} data-help="sales.returnDetail.edit" onClick={() => navigate(`/sales/returns/${r.id}/edit`)}>Edit</Button>}
          {canPost && r.status === 'DRAFT' && <Button variant="contained" disabled={busy} data-help="sales.returnDetail.post" onClick={() => setPostOpen(true)}>Post return</Button>}
          {canManage && r.status === 'POSTED' && r.canCreateCreditNote && !activeNote && <Button variant="contained" disabled={busy} data-help="sales.returnDetail.createCreditNote" onClick={openCreditNote}>Create credit note</Button>}
          {r.status === 'POSTED' && activeNote && (
            <Button data-help="sales.returnDetail.openCreditNote" onClick={() => navigate(`/sales/credit-notes/${activeNote.id}`)}>Credit note {activeNote.creditNoteNumber}</Button>
          )}
          {canManage && r.status === 'POSTED' && <Button color="error" data-help="sales.returnDetail.cancel" onClick={() => setCancelOpen(true)}>Cancel return</Button>}
        </Box>
      </Box>
      {r.status === 'CANCELLED' && r.cancelReason && <Alert severity="info" sx={{ mb: 1 }}>Cancelled: {r.cancelReason}</Alert>}
      {r.status === 'POSTED' && r.deliveredAdjusted && <Alert severity="info" sx={{ mb: 1 }}>Not invoiced: this return reduced the challan's delivered quantity. No credit note applies.</Alert>}
      {r.status === 'DRAFT' && hasDamaged && canSettings && !damagedAccount && (
        <Alert severity="warning" sx={{ mb: 1 }}>Set the damaged-goods account below before posting: damaged lines are written off to it.</Alert>
      )}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 4 }} data-help-section="sales.returnDetail.summary">
        <Typography>Customer: <b>{r.customerName}</b></Typography>
        <Typography>Date: <b>{formatDate(r.returnDate)}</b></Typography>
        <Typography>Store: <b>{r.storeName}</b></Typography>
        <Typography>Order: <b>{r.orderNo}</b></Typography>
        {r.invoiceNumber && <Typography>Invoice: <b>{r.invoiceNumber}</b></Typography>}
        <Typography>Reason: <b>{r.reason}</b></Typography>
        <Typography>Restocked: <b>{formatMoney(r.restockValue)}</b></Typography>
        <Typography>Damaged: <b>{formatMoney(r.damagedValue)}</b></Typography>
      </Paper>
      <Paper sx={{ p: 2, mb: 3 }} data-help-section="sales.returnDetail.lines">
        <Table size="small">
          <TableHead><TableRow>
            <TableCell data-help="sales.returnDetail.lines.col.item">Item</TableCell><TableCell align="right" data-help="sales.returnDetail.lines.col.quantity">Quantity</TableCell><TableCell data-help="sales.returnDetail.lines.col.condition">Condition</TableCell>
            <TableCell align="right" data-help="sales.returnDetail.lines.col.unitCost">Unit cost</TableCell><TableCell align="right" data-help="sales.returnDetail.lines.col.value">Value</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {r.lines.map(l => (
              <React.Fragment key={l.id}>
              <TableRow>
                <TableCell>{l.itemCode ? `${l.itemCode} - ${l.itemName}` : l.description}</TableCell>
                <TableCell align="right">{l.quantity}</TableCell>
                <TableCell>{humanizeEnum(l.condition)}</TableCell>
                <TableCell align="right">{r.status === 'DRAFT' ? '-' : formatMoney(l.unitCost)}</TableCell>
                <TableCell align="right">{r.status === 'DRAFT' ? '-' : formatMoney(l.lineValue)}</TableCell>
              </TableRow>
              {/* F6c: a kit line restocks (or writes off) each component */}
              {r.status !== 'DRAFT' && (l.kitComponents ?? []).map(k => (
                <TableRow key={`${l.id}-${k.componentItemId}`} data-testid="return-kit-component">
                  <TableCell sx={{ pl: 4, color: 'text.secondary' }}>↳ {k.code ? `${k.code} - ${k.name ?? ''}` : 'Component'}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>{k.quantity}{k.unit ? ` ${k.unit}` : ''}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{l.condition === 'RESALEABLE' ? 'Restocked' : 'Written off'}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>-</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatMoney(k.value)}</TableCell>
                </TableRow>
              ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {r.creditNotes.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }} data-help-section="sales.returnDetail.creditNotes">
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Credit notes from this return</Typography>
          {r.creditNotes.map(c => (
            <Box key={c.id} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button data-help="sales.returnDetail.creditNotes.open" onClick={() => navigate(`/sales/credit-notes/${c.id}`)}>{c.creditNoteNumber}</Button>
              <Chip size="small" label={humanizeEnum(c.status)} />
              <Typography>{formatMoney(c.totalAmount)}</Typography>
            </Box>
          ))}
        </Paper>
      )}
      {canSettings && (
        <Paper sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }} data-help-section="sales.returnDetail.damagedAccount">
          <Box sx={{ flex: '1 1 320px', maxWidth: 480 }}>
            <AccountAutocomplete data-help="sales.returnDetail.damagedAccount.account" label="Damaged goods account" accounts={leafAccounts(accounts)} value={damagedAccount}
              onChange={setDamagedAccount} placeholder="None" />
          </Box>
          <Button disabled={busy} data-help="sales.returnDetail.damagedAccount.save" onClick={saveAccount}>Save account</Button>
        </Paper>
      )}
      {/* F6b: serial-tracked lines pick the units coming back from the challan's SOLD serials */}
      <SerialPostDialog open={postOpen} title="Post customer return" confirmLabel="Post" submitting={busy}
        message="Resaleable lines go back into the store at their original cost; damaged lines are written off. The cost of goods sold is reversed."
        lines={r.lines.map(l => ({ lineId: l.id, itemId: l.itemId, label: `${l.itemName ?? l.description ?? 'Item'} (${l.condition === 'RESALEABLE' ? 'resaleable' : 'damaged'})`, quantity: Number(l.quantity), mode: 'pick' as const, soldOnChallanId: r.deliveryChallanId }))}
        onCancel={() => setPostOpen(false)} onConfirm={async (serials) => { if (await run(() => SalesReturnService.post(r.id, serials), 'Return posted', 'Failed to post the return')) setPostOpen(false); }} />
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs" data-help-page="sales.returnDetail.cancel.page">
        <DialogTitle>Cancel return</DialogTitle>
        <DialogContent data-help-section="sales.returnDetail.cancel.section.form" sx={{ pt: 1 }}>
          {r.status === 'POSTED' && r.lines.some(l => l.condition === 'RESALEABLE') && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Cancelling takes the restocked goods out of the store again. If any of the returned units have already been sold on or
              moved, the cancel is refused. For items not tracked by lot the store only needs to still hold the quantity and value,
              so check that these exact units are still there.
            </Alert>
          )}
          <TextField data-help="sales.returnDetail.cancel.reason" label="Reason" fullWidth value={reason} onChange={e => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.returnDetail.cancel.back" onClick={() => setCancelOpen(false)}>Back</Button>
          <Button color="error" data-help="sales.returnDetail.cancel.confirm" disabled={!reason.trim() || busy} onClick={() => { setCancelOpen(false); run(() => SalesReturnService.cancel(r.id, reason), 'Return cancelled', 'Failed to cancel the return'); }}>Cancel return</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!draft} onClose={() => setDraft(null)} fullWidth maxWidth="sm" data-help-page="sales.returnDetail.draftNote.page">
        <DialogTitle>Create credit note from {r.returnNo}</DialogTitle>
        <DialogContent data-help-section="sales.returnDetail.draftNote.section.main" sx={{ pt: 1 }}>
          {draft && (
            <>
              <Typography sx={{ mb: 1 }}>Against invoice <b>{draft.invoiceNumber}</b>, dated {formatDate(draft.noteDate)}. Quantities are at the invoice price and tax code.</Typography>
              <Table size="small" data-help-section="sales.returnDetail.draftNote">
                <TableHead><TableRow><TableCell data-help="sales.returnDetail.draftNote.col.line">Line</TableCell><TableCell align="right" data-help="sales.returnDetail.draftNote.col.qty">Qty</TableCell><TableCell align="right" data-help="sales.returnDetail.draftNote.col.unitPrice">Unit price</TableCell></TableRow></TableHead>
                <TableBody>
                  {draft.lines.map(l => (
                    <TableRow key={l.salesInvoiceLineId}>
                      <TableCell>{l.description}</TableCell><TableCell align="right">{l.quantity}</TableCell>
                      <TableCell align="right">{formatMoney(l.unitPrice)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.returnDetail.draftNote.back" onClick={() => setDraft(null)}>Back</Button>
          <Button variant="contained" disabled={busy} data-help="sales.returnDetail.draftNote.issue" onClick={issueCreditNote}>Issue credit note</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesReturnDetail;
